import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { catalogAddSongPath } from '../../../smartLinks/catalog/addSong/catalogAddSongPath';
import type { ReleaseOption } from '../hooks/useReleaseOptions';
import type { CampaignGoal } from '../hooks/useCampaignActions';
import { defaultGoalOf } from './builderFormat';
import { GoalCards, goalTitleOf, isCampaignGoal } from './GoalCards';
import { QuestionPanel } from './QuestionPanel';
import { SongTiles } from './SongTiles';
import { StepBar } from './StepBar';
import { NEXT_LABELS } from './builderSteps';
import { campaignMessages as messages } from './campaignMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the setup; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Builder.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StepSongGoalProps {
  accountId: number;
  options: ReleaseOption[];
  initialSong: string | null;
  /** The goal of a draft that exists; a new draft takes the default of the song. */
  initialGoal?: string;
  /** Plug pitched the song: the song cannot change any more. */
  songLocked?: boolean;
  onNext: (catalogItemUuid: string, goal: CampaignGoal) => void;
  onCancel: () => void;
  isSaving: boolean;
  failed: 'create' | 'change' | null;
  disabled: boolean;
}

/**
 * Step 1 "Song and goal" (design outreach-v2 b1, b2): two question cards and the step bar. Next
 * makes the draft (`campaigns.create`) or changes the song and goal of the draft.
 */
const StepSongGoalComponent: React.FC<StepSongGoalProps> = ({
  accountId,
  options,
  initialSong,
  initialGoal,
  songLocked = false,
  onNext,
  onCancel,
  isSaving,
  failed,
  disabled,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [song, setSong] = useState<string | null>(initialSong ?? options[0]?.catalogItemUuid ?? null);
  const picked = useMemo(() => options.find((option) => option.catalogItemUuid === song) ?? null, [options, song]);
  const [goal, setGoal] = useState<CampaignGoal | null>(isCampaignGoal(initialGoal) ? initialGoal : null);
  const shownGoal = goal ?? defaultGoalOf(picked?.releaseDate ?? null, new Date());

  return (
    <>
      <QuestionPanel
        title={intl.formatMessage(messages.songTitle)}
        hint={intl.formatMessage(messages.songHint)}
        question={{ number: 1, total: 2 }}
        testId="builder-step-song"
      >
        {options.length === 0 ? (
          <div className={styles.lockCard}>
            <div className={shared.grow}>
              <b>{intl.formatMessage(messages.songNoneTitle)}</b>
              <p className={shared.small}>{intl.formatMessage(messages.songNoneBody)}</p>
            </div>
            <SMLButton
              size="sm"
              color="primary"
              omitCap
              // eslint-disable-next-line no-void
              onClick={() => void navigate(catalogAddSongPath({ from: 'catalog', accountId }))}
              testID="builder-add-song"
            >
              {intl.formatMessage(messages.songAdd)}
            </SMLButton>
          </div>
        ) : (
          <SongTiles options={options} value={song} onChange={setSong} locked={songLocked} />
        )}
        {songLocked && <p className={shared.small}>{intl.formatMessage(messages.songLocked)}</p>}
      </QuestionPanel>
      {options.length > 0 && (
        <QuestionPanel
          title={intl.formatMessage(messages.goalTitle)}
          hint={intl.formatMessage(messages.goalHint)}
          question={{ number: 2, total: 2 }}
        >
          <GoalCards value={shownGoal} onChange={setGoal} />
        </QuestionPanel>
      )}
      {failed !== null && (
        <p className="text-danger mb-0">
          {intl.formatMessage(failed === 'change' ? messages.changeFailed : messages.createFailed)}
        </p>
      )}
      <StepBar
        backKind="cancel"
        onBack={onCancel}
        summary={
          picked === null
            ? undefined
            : intl.formatMessage(messages.navSong, {
                song: picked.title,
                goal: intl.formatMessage(goalTitleOf(shownGoal)).toLowerCase(),
              })
        }
        nextLabel={intl.formatMessage(NEXT_LABELS.who)}
        nextDisabled={disabled || picked === null}
        isSaving={isSaving}
        onNext={() => {
          if (picked !== null) {
            onNext(picked.catalogItemUuid, shownGoal);
          }
        }}
      />
    </>
  );
};

export const StepSongGoal = TypedMemo(StepSongGoalComponent);
